import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Trash2, Plus, Minus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getCart, removeFromCart, updateCartItemQuantity, getCartTotal, getCartCount } from "@/lib/cart";
import type { CartItem } from "@/lib/cart";

interface CartSheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const CartSheet = ({ open: controlledOpen, onOpenChange }: CartSheetProps = {}) => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [internalOpen, setInternalOpen] = useState(false);
  
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const loadCart = () => {
    setCartItems(getCart());
  };

  useEffect(() => {
    loadCart();
    
    // Listen for cart updates
    const handleStorage = () => loadCart();
    window.addEventListener("storage", handleStorage);
    window.addEventListener("cartUpdated", loadCart);
    
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("cartUpdated", loadCart);
    };
  }, []);

  const handleRemove = (itemId: string) => {
    removeFromCart(itemId);
    loadCart();
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const handleQuantityChange = (itemId: string, delta: number) => {
    const item = cartItems.find((i) => i.id === itemId);
    if (item) {
      updateCartItemQuantity(itemId, item.quantity + delta);
      loadCart();
      window.dispatchEvent(new Event("cartUpdated"));
    }
  };

  const handleCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  const itemCount = getCartCount();
  const total = getCartTotal();

  // Dispatch event when cart opens/closes for other components to react
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    window.dispatchEvent(new CustomEvent("cartOpenChange", { detail: { open: newOpen } }));
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <ShoppingCart className="h-5 w-5" />
          {itemCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
              {itemCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-xl">Varukorg</SheetTitle>
          <SheetDescription>
            {itemCount > 0 ? `${itemCount} ${itemCount === 1 ? "produkt" : "produkter"} i varukorgen` : "Din varukorg är tom"}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4">
            {cartItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="rounded-full bg-[hsl(var(--glass-bg))] backdrop-blur-md p-6 mb-4">
                  <ShoppingCart className="h-12 w-12 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground mb-4">Din varukorg är tom</p>
                <Button variant="outline" onClick={() => {
                  setOpen(false);
                  navigate("/explore");
                }}>
                  Börja handla
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {cartItems.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex gap-4 rounded-xl p-4 border border-border/30"
                  >
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="h-24 w-24 object-cover rounded-lg shadow-sm"
                    />
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm line-clamp-1">{item.title}</h4>
                        {item.artistName && (
                          <p className="text-xs text-muted-foreground">{item.artistName}</p>
                        )}
                        {item.variantName && (
                          <p className="text-xs text-muted-foreground">Storlek: {item.variantName}</p>
                        )}
                        <p className="font-bold text-primary mt-2">
                          {item.price.toLocaleString("sv-SE")} kr
                        </p>
                      </div>
                      <div className="flex items-center gap-1 mt-3">
                        <div className="flex items-center gap-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full bg-transparent hover:bg-foreground/10"
                            onClick={() => handleQuantityChange(item.id, -1)}
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full bg-transparent hover:bg-foreground/10"
                            onClick={() => handleQuantityChange(item.id, 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 ml-auto text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemove(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>

        {cartItems.length > 0 && (
          <div className="mt-auto pt-4 space-y-4">
            <div className="space-y-2 px-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Delsumma</span>
                <span className="font-medium">{total.toLocaleString("sv-SE")} kr</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Leverans</span>
                <span className="text-muted-foreground text-xs">Beräknas vid kassan</span>
              </div>
              <div className="h-px bg-border/50 my-2" />
              <div className="flex justify-between font-bold text-lg">
                <span>Totalt</span>
                <span className="text-primary">{total.toLocaleString("sv-SE")} kr</span>
              </div>
            </div>
            <Button className="w-full" size="lg" onClick={handleCheckout}>
              Gå till kassan
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
